import { Link } from 'react-router-dom';
import { DirectusService } from '@/services/directusService';
import { DirectusNews } from '@/lib/directus';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { UniversalContentEditor } from '@/components/universal/UniversalContentEditor';
import { DataFetcher } from '@/components/fallback';
import { useDataFetch } from '@/hooks/useDataFetch';
import { usePageId } from '@/hooks/usePageId';
import { editableProps, useIsVisualEditor } from '@/components/inline/directus-ve';

const Noticias = () => {
  const pageId = usePageId('noticias');
  const editing = useIsVisualEditor();
  const {
    data: news,
    isLoading,
    error,
    refetch
  } = useDataFetch<DirectusNews[]>(
    () => DirectusService.getNews(),
    {
      initialData: [],
      cacheKey: 'news-list',
      staleTime: 60000, // 1 minute cache
      retryCount: 2,
      retryDelay: 1000,
    }
  );

  // Format date function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <>
      <SEOHead
        title="Notícias - Keyprog"
        description="Atualizações do setor, novidades de produtos e casos de estudo."
        keywords="notícias, blog, atualizações, keyprog"
      />

      <main className="container py-12">
        <div className="text-center mb-12">
          <UniversalContentEditor
            collection="pages"
            itemId={pageId ?? ''}
            field="title"
            tag="h1"
            className="text-4xl font-bold text-gradient-primary mb-4"
            value="Notícias"
          />
          <UniversalContentEditor
            collection="pages"
            itemId={pageId ?? ''}
            field="description"
            tag="p"
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            value="Atualizações do setor, novidades de produtos e casos de estudo."
          />
        </div>

        <DataFetcher
          data={news}
          isLoading={isLoading}
          error={error}
          loadingVariant="grid"
          loadingCount={6}
          loadingColumns={3}
          emptyType="documents"
          emptyTitle="Sem notícias disponíveis"
          emptyDescription="Volte em breve para novidades e atualizações."
          onRetry={refetch}
          showContactOnError={false}
        >
          {(newsItems) => (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {newsItems.map((item) => (
                <Card key={item.id} className="overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow group">
                  {item.image && (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img
                        src={DirectusService.getImageUrl(item.image)}
                        alt={item.title || 'News image'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                      />
                    </div>
                  )}

                  <CardHeader>
                    {item.published_date && (
                      <CardDescription className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(item.published_date)}
                      </CardDescription>
                    )}

                    <CardTitle
                      className="group-hover:text-primary transition-colors"
                      {...editableProps(editing, { collection: 'news', item: item.id, fields: 'title' })}
                    >
                      {item.title}
                    </CardTitle>

                    {item.summary && (
                      <p
                        className="text-sm text-muted-foreground line-clamp-3"
                        {...editableProps(editing, { collection: 'news', item: item.id, fields: 'summary' })}
                      >
                        {item.summary}
                      </p>
                    )}

                    {item.category && (
                      <div className="mt-2">
                        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                          <Tag className="h-3 w-3" />
                          {item.category}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>

                  <CardFooter className="mt-auto pt-0">
                    <Button asChild variant="outline" className="w-full group">
                      <Link to={`/noticias/${item.id}`}>
                        Ler Mais
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </DataFetcher>
      </main>
    </>
  );
};

export default Noticias;
